import { snapForShortcut, type SnapKind } from '../snap';
import { toolForShortcut, type ToolId } from './tools';

/** A key press as the adapter sees it, after it has ruled out typing in a text field. */
export interface KeyInput {
  readonly key: string;
  /** Cmd on macOS, Ctrl elsewhere. */
  readonly meta: boolean;
  readonly shift: boolean;
  readonly repeat: boolean;
}

export type KeyCommand =
  | { readonly kind: 'holdOrtho' }
  /** Space held: a left drag pans the view until it is released. */
  | { readonly kind: 'holdPan' }
  | { readonly kind: 'snap'; readonly snap: SnapKind }
  | { readonly kind: 'escape' }
  | { readonly kind: 'toggleOrtho' }
  | { readonly kind: 'cycleToolMode' }
  | { readonly kind: 'tool'; readonly tool: ToolId }
  /** The key is claimed but does nothing, such as a held snap letter repeating. */
  | { readonly kind: 'none' };

export interface KeyBinding {
  readonly command: KeyCommand;
  /** Whether the browser's own meaning for the key (Space scrolling, Tab) is suppressed. */
  readonly preventDefault: boolean;
}

/** What the resolver needs to know about the session. */
export interface KeyContext {
  readonly placingPoint: boolean;
  /** Whether the first tool, Select, is active; Shift only holds ortho in the others. */
  readonly atFirstTool: boolean;
  readonly ortho: boolean;
  /** Whether the active tool has modes for Tab to cycle. */
  readonly toolModes: boolean;
}

/**
 * Matched exactly, so Shift+O is not O. View changes are buttons, except Space, which
 * drawing apps hold to pan.
 */
const FIXED_KEYS: Readonly<Record<string, KeyBinding>> = {
  ' ': { command: { kind: 'holdPan' }, preventDefault: true },
  Escape: { command: { kind: 'escape' }, preventDefault: false },
  o: { command: { kind: 'toggleOrtho' }, preventDefault: false },
};

const bind = (command: KeyCommand, preventDefault = false): KeyBinding => ({
  command,
  preventDefault,
});

const sameCommand = (a: KeyCommand, b: KeyCommand) => {
  const fields = Object.entries(a) as [keyof KeyCommand, unknown][];
  return Object.keys(b).length === fields.length && fields.every(([k, v]) => b[k] === v);
};

const fixedKeyName = (key: string) => (key === ' ' ? 'Space' : key.toUpperCase());

/** The key that triggers a command, as a button shows it, or null when no key does. */
export function keyLabel(command: KeyCommand): string | null {
  const fixed = Object.entries(FIXED_KEYS).find(([, binding]) =>
    sameCommand(binding.command, command),
  );
  return fixed ? fixedKeyName(fixed[0]) : null;
}

/**
 * What a key does, or null when nothing claims it. Layers are tried in order, and the first
 * that claims the key wins: holding Shift, snap letters while a point is being placed, Tab
 * while the tool has modes, fixed keys, then tool letters. Cmd chords other than
 * `historyKey`'s are left to the browser.
 */
export function resolveKey(input: KeyInput, ctx: KeyContext): KeyBinding | null {
  if (input.key === 'Shift' && !ctx.atFirstTool && !ctx.ortho) return bind({ kind: 'holdOrtho' });

  if (input.meta) return null;

  const snap = ctx.placingPoint ? snapForShortcut(input.key) : null;
  if (snap) return bind(input.repeat ? { kind: 'none' } : { kind: 'snap', snap });

  if (input.key === 'Tab' && ctx.toolModes) {
    return bind(input.repeat ? { kind: 'none' } : { kind: 'cycleToolMode' }, true);
  }

  const fixed = FIXED_KEYS[input.key];
  if (fixed) return fixed;

  const tool = toolForShortcut(input.key);
  return tool ? bind({ kind: 'tool', tool }) : null;
}

/**
 * Whether a key steps through the drawing's one undo history. It is checked before
 * anything else, in text fields and the code editor too, so no field keeps its own history.
 */
export function historyKey(input: KeyInput): 'undo' | 'redo' | null {
  if (!input.meta) return null;
  const key = input.key.toLowerCase();
  if (key === 'z') return input.shift ? 'redo' : 'undo';
  return key === 'y' && !input.shift ? 'redo' : null;
}

/** Whether a pointer press pans the view rather than going to the active tool. */
export const pansView = (button: number, alt: boolean, spaceHeld: boolean): boolean =>
  button === 1 || (button === 0 && (alt || spaceHeld));

/** A wheel event as the adapter sees it. */
export interface WheelInput {
  readonly deltaX: number;
  readonly deltaY: number;
  /** `WheelEvent.deltaMode`: 0 pixels, 1 lines, 2 pages. */
  readonly deltaMode: number;
  /** Ctrl or Cmd held; a trackpad pinch also arrives with Ctrl set. */
  readonly zoom: boolean;
}

export type WheelGesture =
  | { readonly kind: 'pan'; readonly dx: number; readonly dy: number }
  | { readonly kind: 'zoom'; readonly factor: number };

const DELTA_PIXELS = [1, 40, 800];
/** Caps one mouse notch, which jumps about 100px, near a pinch's few pixels per event. */
const MAX_ZOOM_DELTA = 25;

/** Scrolling pans, on a mouse or a trackpad; Ctrl or Cmd with it, or a pinch, zooms. */
export function wheelGesture(input: WheelInput): WheelGesture {
  const scale = DELTA_PIXELS[input.deltaMode] ?? 1;
  const dx = input.deltaX * scale;
  const dy = input.deltaY * scale;
  if (!input.zoom) return { kind: 'pan', dx: -dx, dy: -dy };
  const capped = Math.max(-MAX_ZOOM_DELTA, Math.min(MAX_ZOOM_DELTA, dy));
  return { kind: 'zoom', factor: Math.exp(-capped * 0.01) };
}
